import {Component, OnInit} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {map, shareReplay, skipWhile, take} from 'rxjs/operators';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import memo from 'memo-decorator';
import {ActivatedRoute} from '@angular/router';
import {AnalyticsServiceNameModel, TelemetryService} from 'telemetry-library';

import FundInvestmentStrategyType from 'src/app/shared/enums/FundInvestmentStrategyType.enum';
import {RoutingService} from 'src/app/services/routing.service';
import {TerraUtils} from 'src/app/shared/TerraUtils';
import {LpFundService} from './lp-fund.service';
import {LpHoldingService} from '../../shared/holdings/lp-holding.service';
import {LpUiService} from '../../shared/lp-ui.service';
import HoldingStatus from 'src/app/shared/enums/HoldingStatus.enum';
import HoldingDiscriminator from 'src/app/shared/enums/HoldingDiscriminator.enum';

@Component({
  selector: 'terra-lp-fund',
  templateUrl: './lp-fund.component.html',
  styleUrls: ['./lp-fund.component.scss'],
  providers: [LpFundService,
    {provide: LpHoldingService, useExisting: LpFundService}
  ],
})
export class LpFundComponent extends OnDestroyMixin implements OnInit {

  // consts
  FundInvestmentStrategy = FundInvestmentStrategyType;
  EXAMPLE_ASSET_ID = TerraUtils.consts.EXAMPLE_ASSET_ID;
  HoldingStatus = HoldingStatus;
  opportunityCode: string;
  isOpportunity: boolean;

  // enums
  HoldingDiscriminator = HoldingDiscriminator;

  get holdingsPageUrl() {
    return this.routingService.lpHoldingsPage;
  }

  fund$ = this.lpFundService.fund$;
  originFundId$ = this.lpFundService.originFundId$;
  originFundBasicInfo$ = this.lpFundService.originFundBasicInfo$;
  isFunded$ = this.fund$.pipe(map(fund => fund.status === HoldingStatus.Owned || !this.opportunityCode), shareReplay(1));

  constructor(
    private routingService: RoutingService,
    private lpFundService: LpFundService,
    private lpUiService: LpUiService,
    private titleService: Title,
    private telemetryService: TelemetryService,
    private activatedRoute: ActivatedRoute) {
    super();
  }

  ngOnInit() {
    // opportunity code of marketing deck
    this.opportunityCode = this.activatedRoute.snapshot.queryParams.oppCode;
    this.isOpportunity = this.opportunityCode !== undefined;
    // Set tab title
    this.fund$
      .pipe(untilComponentDestroyed(this), skipWhile(fund => !fund))
      .subscribe(fund => {
        this.titleService.setTitle('Fund Details: ' + fund.name + TerraUtils.consts.GpPageTitleSuffix);
      });

    this.addViewTelemetryEvent();
  }

  @memo()
  backUrl(originFundId: number) {
    if (originFundId) {
      return this.routingService.lpFund(originFundId);
    }

    return this.holdingsPageUrl;
  }

  @memo()
  getFundPageUrl(fundId: number) {
    return this.routingService.lpFund(fundId);
  }

  getBankAccountEditPage(fundId: number) {
    // return this.routingService.holdingBankAccountPreferenceEditor(fundId);
    return this.routingService.lpBankAccounts;
  }

  getMarketingDeckPage() {
    return this.routingService.lpOfferingDeck(this.opportunityCode);
  }

  private addViewTelemetryEvent() {
    let eventId, eventTitle;
    this.fund$.pipe(take(1)).subscribe(fund => {
      switch (fund.status) {
        case HoldingStatus.Fundraising: {
          if (this.isOpportunity) {
            eventId = '209';
            eventTitle = 'LP FUND OPPORTUNITY VIEW';
          } else {
            eventId = '208';
            eventTitle = 'LP PENDING FUND VIEW';
          }
          break;
        }
        case HoldingStatus.Owned: {
          eventId = '9';
          eventTitle = 'LP FUND VIEW';
          break;
        }
        default:
          return;
      }

      this.lpFundService.holdingId$.pipe(take(1)).subscribe(fundId => {
        this.telemetryService.create({
          eventID: eventId,
          eventTitle: eventTitle,
          holdingID: fundId,
          additional: {
            isMobileDevice: TerraUtils.isMobileDevice()
          }
        }, AnalyticsServiceNameModel.Insights);
      });
    });
  }
}
